import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function validateEmail(email: string): boolean {
  if (!email.trim()) return true;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Format a number as currency with the appropriate symbol.
 * Uses Intl.NumberFormat for proper locale-aware formatting.
 * 
 * @param amount - The amount to format
 * @param currency - ISO 4217 currency code (e.g., 'USD', 'EUR', 'JPY')
 * @returns Formatted currency string (e.g., '$10.00', '€10.00', '¥1000')
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  try {
    // Use 'en-US' locale as base but let the currency determine the symbol
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      // For zero-decimal currencies like JPY, don't show decimal places
      minimumFractionDigits: isZeroDecimalCurrency(currency) ? 0 : 2,
      maximumFractionDigits: isZeroDecimalCurrency(currency) ? 0 : 2,
    }).format(amount);
  } catch {
    // Fallback if currency code is invalid
    return `$${amount.toFixed(2)}`;
  }
}

/**
 * Check if a currency is a zero-decimal currency (no cents/minor units)
 */
function isZeroDecimalCurrency(currency: string): boolean {
  const zeroDecimalCurrencies = [
    'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA',
    'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'
  ];
  return zeroDecimalCurrencies.includes(currency.toUpperCase());
}

export const validateImageUrl = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!url || typeof url !== 'string') {
      resolve(false);
      return;
    }
    
    let settled = false;
    let timeoutId: NodeJS.Timeout | null = null;
    let resolveCallback: ((value: boolean) => void) | null = resolve;
    
    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      // Clear event handlers by nulling the image references
      if (img) {
        img.onload = null;
        img.onerror = null;
      }
    };
    
    const settle = (result: boolean) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (resolveCallback) {
        resolveCallback(result);
        // Null out callback to prevent any potential future calls
        resolveCallback = null;
      }
    };
    
    const img = new Image();
    img.onload = () => settle(true);
    img.onerror = () => settle(false);
    img.src = url;
    
    // Timeout after 5 seconds
    timeoutId = setTimeout(() => {
      settle(false);
    }, 5000);
  });
};
